import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { getSubscriptionDetails, updatePaymentMethod, createSetupIntent } from '../services/subscriptionService';
import './PaymentMethod.css';

// Load Stripe outside of component render cycle
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function PaymentForm({ subscriptionId, subscription, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const stripe = useStripe();
  const elements = useElements();

  // Get setup intent for saving the card
  useEffect(() => {
    const getSetupIntent = async () => {
      try {
        const setupIntent = await createSetupIntent();
        setClientSecret(setupIntent.client_secret);
      } catch (error) {
        console.error('Error getting setup intent:', error);
        setErrorMessage('Failed to initialize payment form. Please try again.');
        onError(error);
      }
    };

    getSetupIntent();
  }, [onError]);

  // Pre-fill form with subscription data
  useEffect(() => {
    if (subscription) {
      setBillingDetails(prevDetails => ({
        ...prevDetails,
        name: subscription.customer_name || '',
        email: subscription.customer_email || '',
      }));
    }
  }, [subscription]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setBillingDetails(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    // Validate form
    if (!billingDetails.name) {
      setErrorMessage('Please enter your name');
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Create payment method and confirm setup intent
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: billingDetails.name,
            email: billingDetails.email,
            address: billingDetails.address
          },
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message);
        setLoading(false);
        return;
      }

      // Update subscription with the new payment method
      await updatePaymentMethod(subscriptionId, result.setupIntent.payment_method);

      // Call the success handler
      onSuccess();
    } catch (error) {
      console.error('Payment update error:', error);
      setErrorMessage(error.detail || 'Failed to update payment method');
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="form-section">
        <h3>Card Information</h3>
        <div className="card-element-container">
          <label htmlFor="card-element">Credit or debit card</label>
          <div className="card-element">
            <CardElement id="card-element" options={cardElementOptions} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Billing Details</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Name on Card</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Jane Doe"
              required
              value={billingDetails.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="jane.doe@example.com"
              required
              value={billingDetails.email}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address.line1">Address</label>
          <input
            id="address.line1"
            name="address.line1"
            placeholder="123 Main St."
            required
            value={billingDetails.address.line1}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.city">City</label>
            <input
              id="address.city"
              name="address.city"
              placeholder="San Francisco"
              required
              value={billingDetails.address.city}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.state">State</label>
            <input
              id="address.state"
              name="address.state"
              placeholder="CA"
              required
              value={billingDetails.address.state}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.postal_code">ZIP / Postal Code</label>
            <input
              id="address.postal_code"
              name="address.postal_code"
              placeholder="94103"
              required
              value={billingDetails.address.postal_code}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.country">Country</label>
            <select
              id="address.country"
              name="address.country"
              value={billingDetails.address.country}
              onChange={handleInputChange}
              required
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-danger">
          {errorMessage}
        </div>
      )}

      <div className="form-actions">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={!stripe || loading}
        >
          {loading ? "Processing..." : "Update Payment Method"}
        </button>
      </div>
    </form>
  );
}

function PaymentMethod() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  const { id: subscriptionId } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const subscriptionData = await getSubscriptionDetails(subscriptionId);
        setSubscription(subscriptionData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching subscription details:', error);
        setError(error.detail || 'Failed to load subscription details');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [subscriptionId]);
  
  const handleSuccess = () => {
    setUpdateSuccess(true);
    
    // Navigate back to profile after success
    setTimeout(() => {
      navigate('/profile');
    }, 2000);
  };
  
  const handleError = (error) => {
    console.error('Payment method update error:', error);
    setError(error.detail || 'Failed to update payment method');
  };
  
  if (loading) {
    return <div className="loading-container">Loading payment details...</div>;
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => navigate('/profile')}>
          Back to Profile
        </button>
      </div>
    );
  }
  
  if (updateSuccess) {
    return (
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h2>Payment Method Updated!</h2>
        <p>Your payment method has been successfully updated.</p>
        <p>Returning to profile...</p>
      </div>
    );
  }
  
  return (
    <div className="payment-method-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <h1>Update Payment Method</h1>
            {subscription && (
              <p className="subscription-info">
                Subscription: <strong>{subscription.package_name}</strong>
              </p>
            )}
          </div>
          <Link to="/profile" className="btn btn-outline back-button">
            Back to Profile
          </Link>
        </div>
        
        <div className="payment-form-container">
          {subscription && subscription.payment_method && (
            <div className="current-payment-method">
              <h3>Current Payment Method</h3>
              <div className="payment-method-details">
                <p>
                  <span className="payment-label">Card:</span>
                  <span className="payment-value">
                    {subscription.payment_method.brand} •••• {subscription.payment_method.last4}
                  </span>
                </p>
                <p>
                  <span className="payment-label">Expiration:</span>
                  <span className="payment-value">
                    {subscription.payment_method.exp_month}/{subscription.payment_method.exp_year}
                  </span>
                </p>
              </div>
            </div>
          )}
          
          <div className="new-payment-method">
            <h3>New Payment Method</h3>
            <Elements stripe={stripePromise}>
              <PaymentForm 
                subscriptionId={subscriptionId}
                subscription={subscription}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentMethod; 